// Siktlinje med REELL geometri: jordkrumming + atmosfærisk refraksjon, rikt
// resultat (hvilket punkt blokkerer, klaring, nødvendig masthøyde,
// maskeringsvinkel) og sektor-viewshed slik en OP-post/radar faktisk dekker.
//
// Bygger PÅ `heightProfile`/`elevationAt` fra @skade/contracts — vi duplikerer
// ingen DEM-algoritmer, vi legger til det contracts' `lineOfSight` mangler:
//
//   1. Jordkrumming og refraksjon. contracts' `lineOfSight` er plan geometri.
//      Over ~10 km er det direkte feil: jorda faller bort under siktlinja med
//      13 m på 30 km (k=4/3), 37 m på 50 km. Uten korreksjonen «ser» man over
//      horisonten.
//   2. HVOR det blokkerer, hvor mye klaring man har, og hvor høyt man må opp.
//   3. Sektor + maks rekkevidde i viewshed — en OP-post har en observasjons-
//      sektor, en radar en antennesektor og en instrumentert rekkevidde.
//
// ── Formler ──────────────────────────────────────────────────────────────────
// Effektiv jordradius:  R_e = k · R,  k = 4/3 (standard atmosfære, ITU-R P.834 /
//   normal refraksjonsgradient −39 N-enheter/km). k<1 = subrefraksjon (kaldt
//   hav, dårligste tilfelle for samband), k>1 = superrefraksjon/duktdannelse.
// Krumnings-/refraksjonskorreksjon («earth bulge») for et punkt d1 fra
//   observatør og d2 fra mål:  Δh = d1·d2 / (2·R_e)
//   Denne legges TIL terrenghøyden, så siktlinja kan behandles som en rett linje
//   mellom endepunktene («flat-jord-ramme»).
// Ekvivalent tangentplan-form brukt i den radielle sveipen:
//   jordfall ved avstand d fra observatør:  Δh = d² / (2·R_e)
//   og synlighet avgjøres på elevasjonsvinkler. De to formene gir EKSAKT samme
//   svar (differansen er et ledd som er lineært i d og derfor forsvinner i
//   vinkelsammenligningen) — bevist i los.test.ts.
import { elevationAt, haversineM, heightProfile, project, } from '@skade/contracts';
/** Standard k-faktor for normal atmosfære (ITU-R). */
export const DEFAULT_K_FACTOR = 4 / 3;
/** Jordradius (m) — samme verdi som geo.ts bruker, så avstander er konsistente. */
export const EARTH_RADIUS_M = 6_371_000;
const DEG = 180 / Math.PI;
/** Effektiv jordradius R_e = k · R. */
export function effectiveEarthRadiusM(kFactor = DEFAULT_K_FACTOR, earthRadiusM = EARTH_RADIUS_M) {
    if (!(kFactor > 0))
        throw new Error('effectiveEarthRadiusM: k må være > 0');
    return kFactor * earthRadiusM;
}
/**
 * Krumnings- + refraksjonskorreksjon for et mellompunkt: Δh = d1·d2 / (2·k·R).
 * Legges til terrenghøyden. Maksimal midtveis (d1 = d2 = D/2) → D²/(8·R_e).
 */
export function curvatureBulgeM(d1M, d2M, kFactor = DEFAULT_K_FACTOR, earthRadiusM = EARTH_RADIUS_M) {
    return (d1M * d2M) / (2 * effectiveEarthRadiusM(kFactor, earthRadiusM));
}
/**
 * Jordfall relativt observatørens tangentplan: Δh = d² / (2·k·R).
 * Brukes når synlighet avgjøres på elevasjonsvinkler fra observatøren.
 */
export function earthDropM(dM, kFactor = DEFAULT_K_FACTOR, earthRadiusM = EARTH_RADIUS_M) {
    return (dM * dM) / (2 * effectiveEarthRadiusM(kFactor, earthRadiusM));
}
/**
 * Siktlinje fra observatør til mål med jordkrumming/refraksjon.
 *
 * Returnerer null hvis observatørens eller målets BAKKEHØYDE ikke finnes i
 * rutenettet (hull eller utenfor) — da har vi ikke grunnlag for et svar, og et
 * `false` ville vært en løgn på linje med contracts' `lineOfSight`.
 *
 * Endepunktene holdes utenfor hindringsvurderingen (der er terrenget per
 * definisjon lik bakkehøyden, og klaringen lik oppgitt antenne-/målhøyde).
 */
export function lineOfSightDetailed(g, from, to, opts = {}) {
    const observerHeightM = opts.observerHeightM ?? 1.7;
    const targetHeightM = opts.targetHeightM ?? 0;
    const samples = Math.max(2, Math.floor(opts.samples ?? 256));
    const kFactor = opts.kFactor ?? DEFAULT_K_FACTOR;
    const earthRadiusM = opts.earthRadiusM ?? EARTH_RADIUS_M;
    const required = opts.requiredClearanceM ?? 0;
    const e0 = elevationAt(g, from.lat, from.lon);
    const e1 = elevationAt(g, to.lat, to.lon);
    if (e0 === null || e1 === null)
        return null;
    const distanceM = haversineM(from, to);
    const observerAmslM = e0 + observerHeightM;
    const targetAmslM = e1 + targetHeightM;
    const Re = effectiveEarthRadiusM(kFactor, earthRadiusM);
    const profile = heightProfile(g, from, to, samples);
    const out = [];
    let minClearanceM = Number.POSITIVE_INFINITY;
    let minClearanceAtM = 0;
    let obstruction = null;
    let worstPenetration = 0;
    let extraNeeded = 0;
    let maskingTan = Number.NEGATIVE_INFINITY;
    let voidSamples = 0;
    for (let i = 0; i < profile.length; i++) {
        const p = profile[i];
        const d1 = p.distanceM;
        const d2 = Math.max(0, distanceM - d1);
        const bulgeM = (d1 * d2) / (2 * Re);
        const t = distanceM > 0 ? d1 / distanceM : 0;
        const sightElevM = observerAmslM + (targetAmslM - observerAmslM) * t;
        const terrainElevM = p.elevM;
        const effectiveElevM = terrainElevM === null ? null : terrainElevM + bulgeM;
        const clearanceM = effectiveElevM === null ? null : sightElevM - effectiveElevM;
        out.push({
            distanceM: d1,
            lat: p.lat,
            lon: p.lon,
            terrainElevM,
            bulgeM,
            effectiveElevM,
            sightElevM,
            clearanceM,
        });
        if (terrainElevM === null) {
            voidSamples++;
            continue;
        }
        // Endepunktene sier ingenting om hindringer.
        if (i === 0 || i === profile.length - 1)
            continue;
        if (clearanceM !== null && clearanceM < minClearanceM) {
            minClearanceM = clearanceM;
            minClearanceAtM = d1;
        }
        const penetration = -(clearanceM ?? 0);
        if (penetration > worstPenetration) {
            worstPenetration = penetration;
            obstruction = {
                distanceM: d1,
                lat: p.lat,
                lon: p.lon,
                terrainElevM,
                effectiveElevM: effectiveElevM,
                sightElevM,
                penetrationM: penetration,
            };
        }
        // Å heve observatøren Δ hever siktlinja med Δ·(1−t) i dette punktet.
        const deficit = required - (clearanceM ?? 0);
        if (deficit > 0 && t < 1)
            extraNeeded = Math.max(extraNeeded, deficit / (1 - t));
        // Maskeringsvinkel i tangentplan-formen: terrenget minus jordfall.
        if (d1 > 0) {
            const tan = (terrainElevM - (d1 * d1) / (2 * Re) - observerAmslM) / d1;
            if (tan > maskingTan)
                maskingTan = tan;
        }
    }
    if (!Number.isFinite(minClearanceM)) {
        // Ingen brukbare mellompunkt (alt er hull) — rapportér ærlig som ukjent.
        minClearanceM = Number.NaN;
    }
    const targetTan = distanceM > 0
        ? (targetAmslM - (distanceM * distanceM) / (2 * Re) - observerAmslM) / distanceM
        : 0;
    return {
        clear: Number.isNaN(minClearanceM) ? false : minClearanceM >= required,
        distanceM,
        observerGroundElevM: e0,
        observerAmslM,
        targetGroundElevM: e1,
        targetAmslM,
        minClearanceM,
        minClearanceAtM,
        obstruction,
        requiredObserverHeightM: observerHeightM + extraNeeded,
        extraObserverHeightM: extraNeeded,
        maskingAngleDeg: Number.isFinite(maskingTan) ? Math.atan(maskingTan) * DEG : -90,
        targetAngleDeg: Math.atan(targetTan) * DEG,
        voidSamples,
        samples: out,
    };
}
export function minimumUnmaskedAltitude(g, from, to, opts = {}) {
    // Kjør med målhøyde 0 for å få den rene terreng-maskeringsvinkelen.
    const los = lineOfSightDetailed(g, from, to, { ...opts, targetHeightM: 0 });
    if (!los)
        return null;
    const kFactor = opts.kFactor ?? DEFAULT_K_FACTOR;
    const earthRadiusM = opts.earthRadiusM ?? EARTH_RADIUS_M;
    const D = los.distanceM;
    const maskTan = Math.tan(los.maskingAngleDeg / DEG);
    const minAmsl = los.observerAmslM + D * maskTan + earthDropM(D, kFactor, earthRadiusM);
    return {
        distanceM: D,
        minAltitudeAmslM: Math.max(los.targetGroundElevM, minAmsl),
        minAltitudeAglM: Math.max(0, minAmsl - los.targetGroundElevM),
        targetGroundElevM: los.targetGroundElevM,
        maskingAngleDeg: los.maskingAngleDeg,
        maskedBy: los.obstruction,
    };
}
// ── Sektor-viewshed (radiell sveip) ──────────────────────────────────────────
/** Normaliser asimut til [0,360). */
export function normalizeAzimuthDeg(az) {
    const a = az % 360;
    return a < 0 ? a + 360 : a;
}
/** Sektorbredde med klokka fra→til. `from === to` tolkes som hele sirkelen. */
export function sectorSpanDeg(fromAzimuthDeg, toAzimuthDeg) {
    const span = normalizeAzimuthDeg(toAzimuthDeg - fromAzimuthDeg);
    return span === 0 ? 360 : span;
}
/**
 * Radiell sveip fra observatøren: for hver asimut marsjeres det utover mens
 * høyeste terreng-elevasjonsvinkel så langt huskes. Et punkt er synlig når dets
 * egen elevasjonsvinkel (inkl. målhøyde) er ≥ maksimum SÅ LANGT — den klassiske
 * O(asimut·steg)-algoritmen, i motsetning til contracts' `viewshed` som kjører
 * full LOS per celle (O(n²·samples)).
 *
 * Jordkrumming/refraksjon tas inn som jordfall d²/(2·R_e) i vinkelen.
 *
 * MONOTONI: å heve observatøren kan aldri gi mindre sikt. For en blokkering i d1
 * og et mål i d2 > d1 er synlighetsbetingelsen
 *   d1·(h_T − fall_2) − d2·(h_1 − fall_1) ≥ (d1 − d2)·O
 * og siden d1 − d2 < 0 blir høyresiden mindre når O vokser. Testet i los.test.ts.
 */
function sweep(g, observer, opts, onHit) {
    const observerHeightM = opts.observerHeightM ?? 1.7;
    const targetHeightM = opts.targetHeightM ?? 0;
    const maxRangeM = opts.maxRangeM;
    const minRangeM = Math.max(0, opts.minRangeM ?? 0);
    const kFactor = opts.kFactor ?? DEFAULT_K_FACTOR;
    const earthRadiusM = opts.earthRadiusM ?? EARTH_RADIUS_M;
    const Re = effectiveEarthRadiusM(kFactor, earthRadiusM);
    if (!(maxRangeM > 0))
        throw new Error('sweep: maxRangeM må være > 0');
    const ground = elevationAt(g, observer.lat, observer.lon);
    if (ground === null)
        return null;
    const observerAmslM = ground + observerHeightM;
    const rangeStepM = Math.max(1, opts.rangeStepM ?? g.cellSizeM / 2);
    // Standard asimut-steg: bue ≈ halv celle ved maks rekkevidde. Klemmingen
    // gjelder BARE det utledede steget (så vi ikke sporer 100 000 stråler) — et
    // eksplisitt oppgitt steg respekteres som oppgitt.
    const azimuthStepDeg = opts.azimuthStepDeg !== undefined
        ? opts.azimuthStepDeg
        : Math.max(0.25, Math.min(2, ((g.cellSizeM * 0.5) / maxRangeM) * DEG));
    if (!(azimuthStepDeg > 0))
        throw new Error('sweep: azimuthStepDeg må være > 0');
    const from = normalizeAzimuthDeg(opts.fromAzimuthDeg ?? 0);
    const span = sectorSpanDeg(opts.fromAzimuthDeg ?? 0, opts.toAzimuthDeg ?? 360);
    const rays = Math.max(1, Math.ceil(span / azimuthStepDeg));
    const horizon = [];
    for (let a = 0; a < rays; a++) {
        const azimuthDeg = normalizeAzimuthDeg(from + (span * a) / rays);
        let maskTan = Number.NEGATIVE_INFINITY;
        let maskRangeM = 0;
        let maskElevM = Number.NaN;
        for (let d = rangeStepM; d <= maxRangeM + 1e-9; d += rangeStepM) {
            const p = project(observer, azimuthDeg, d);
            const terrainElevM = elevationAt(g, p.lat, p.lon);
            if (terrainElevM === null)
                continue; // hull/utenfor: ingen påstand
            const drop = (d * d) / (2 * Re);
            const targetAmsl = opts.targetAltitudeAmslM !== undefined
                ? opts.targetAltitudeAmslM
                : terrainElevM + targetHeightM;
            const targetTan = (targetAmsl - drop - observerAmslM) / d;
            const visible = d >= minRangeM && targetTan >= maskTan;
            onHit({ azimuthDeg, rangeM: d, lat: p.lat, lon: p.lon, terrainElevM, visible });
            const terrainTan = (terrainElevM - drop - observerAmslM) / d;
            if (terrainTan > maskTan) {
                maskTan = terrainTan;
                maskRangeM = d;
                maskElevM = terrainElevM;
            }
        }
        horizon.push({
            azimuthDeg,
            maskingAngleDeg: Number.isFinite(maskTan) ? Math.atan(maskTan) * DEG : -90,
            atRangeM: maskRangeM,
            elevM: maskElevM,
        });
    }
    return { observerGroundElevM: ground, observerAmslM, horizon };
}
/**
 * Viewshed for OP-post/sensor: jordkrumming, sektor-begrensning (fra→til asimut)
 * og maks rekkevidde. Resultatet deler geo-referanse med inn-rutenettet.
 */
export function sectorViewshed(g, observer, opts) {
    const visible = new Uint8Array(g.rows * g.cols);
    const touched = new Uint8Array(g.rows * g.cols);
    let raysTraced = 0;
    let lastAz = Number.NaN;
    const res = sweep(g, observer, opts, (hit) => {
        if (hit.azimuthDeg !== lastAz) {
            lastAz = hit.azimuthDeg;
            raysTraced++;
        }
        const row = Math.round((g.north - hit.lat) / g.latStep);
        const col = Math.round((hit.lon - g.west) / g.lonStep);
        if (row < 0 || col < 0 || row >= g.rows || col >= g.cols)
            return;
        const idx = row * g.cols + col;
        touched[idx] = 1;
        if (hit.visible)
            visible[idx] = 1;
    });
    if (!res)
        return null;
    let cellsVisible = 0;
    let cellsTouched = 0;
    for (let i = 0; i < visible.length; i++) {
        if (visible[i])
            cellsVisible++;
        if (touched[i])
            cellsTouched++;
    }
    return {
        rows: g.rows,
        cols: g.cols,
        north: g.north,
        west: g.west,
        latStep: g.latStep,
        lonStep: g.lonStep,
        visible,
        observer,
        observerGroundElevM: res.observerGroundElevM,
        observerAmslM: res.observerAmslM,
        maxRangeM: opts.maxRangeM,
        minRangeM: Math.max(0, opts.minRangeM ?? 0),
        fromAzimuthDeg: normalizeAzimuthDeg(opts.fromAzimuthDeg ?? 0),
        toAzimuthDeg: normalizeAzimuthDeg(opts.toAzimuthDeg ?? 360),
        cellsVisible,
        cellsTouched,
        raysTraced,
        horizon: res.horizon,
    };
}
/** Er cellen som dekker (lat,lon) synlig i et viewshed-resultat? */
export function isVisibleAt(v, lat, lon) {
    const row = Math.round((v.north - lat) / v.latStep);
    const col = Math.round((lon - v.west) / v.lonStep);
    if (row < 0 || col < 0 || row >= v.rows || col >= v.cols)
        return false;
    return v.visible[row * v.cols + col] === 1;
}
/**
 * Terreng-horisont rundt en OP-post/antenne: maskeringsvinkel per asimut.
 * Dette er «hvor høyt over horisonten må noe være for å bli sett» — grunnlaget
 * for både RF-dekning og radar-lavhøydeanalyse.
 */
export function terrainHorizon(g, observer, opts) {
    const res = sweep(g, observer, opts, () => { });
    return res ? res.horizon : null;
}
//# sourceMappingURL=los.js.map